import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { costApplicationStatusVariant } from '@/lib/status-variants'
import { useRouter } from '../../../hooks'
import { formatIsoDate } from '../../schedule/lib/format'
import ApplicationDialog from '../applications/ApplicationDialog'
import { useApplicationMutations } from '../hooks/useApplicationMutations'
import { newApplicationBlocker } from '../lib/applications'
import { APPLICATION_STATUS_KEY } from '../lib/labels'
import { compareMoneyStrings } from '../lib/contracts'
import { formatMoney } from '../lib/money'
import type { CostApplication, CostContract } from '../types'

interface Props { contract: CostContract; applications: CostApplication[]; projectId: string; currency: string; locale: string; canCreate: boolean }

/** A works contract's payment applications, newest first (1b-A spec §12). */
const ContractApplicationsCard: React.FC<Props> = ({ contract, applications, projectId, currency, locale, canCreate }) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const mutations = useApplicationMutations(projectId, contract.id)
  const [creating, setCreating] = useState(false)
  const money = (v: string) => formatMoney(v, { locale, currency })
  const showNew = canCreate
  const blocker = newApplicationBlocker(contract, applications)
  const base = `/project/${projectId}/cost-control`
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_contract_applications_title')}</CardTitle>
        {showNew && (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)} disabled={!!blocker}>
            <Plus className="h-4 w-4" />
            {t('CostControl_application_new')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {showNew && blocker && <p className="text-xs text-muted-foreground">{t(blocker)}</p>}
        {applications.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_contract_applications_empty')}</p>}
        {applications.length > 0 && (
          <Table>
            <TableBody>
              {applications.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Link className="whitespace-nowrap text-primary hover:underline" to={`${base}/applications/${a.id}`}>{t('CostControl_application_title', { number: a.number })}</Link>
                    <div className="text-xs text-muted-foreground">{formatIsoDate(t, a.periodFrom)} – {formatIsoDate(t, a.periodTo)}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={costApplicationStatusVariant(a.status)} className="whitespace-nowrap">{t(APPLICATION_STATUS_KEY[a.status])}</Badge>
                    {a.invoiceId && (
                      <div className="mt-1 text-xs"><Link className="text-primary hover:underline" to={`${base}/invoices/${a.invoiceId}`}>{a.invoiceNumber}</Link></div>
                    )}
                    {a.investorInvoice && <div className="mt-1 text-xs text-muted-foreground">{a.investorInvoice.number}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="whitespace-nowrap tabular-nums">{money(a.requested)}</div>
                    {a.status === 'CERTIFIED' && compareMoneyStrings(a.certified, a.requested) !== 0 && (
                      <div className="text-xs tabular-nums text-muted-foreground">{t('CostControl_application_status_CERTIFIED')} {money(a.certified)}</div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <ApplicationDialog
        open={creating}
        mode="create"
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) history.push(`${base}/applications/${res.data.createCostApplication.id}`)
          return res.ok
        }}
      />
    </Card>
  )
}
export default ContractApplicationsCard
